import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  AbrirComandaDialog, CancelarComandaDialog, ComandaAberta, JuntarDialog, ListaComandas, NovaComandaDialog, VerComandaDialog, comandasApi, erroDe,
  imprimirFechamentoDaComanda, imprimirPreparoDaComanda, useComandas,
} from "../features/comandas";
import { FechamentoPedidoDialog, fechamentoDoPedido } from "../features/mesas-fechamento";
import { balancaLigadaAqui, useConfigBalanca, usePrefsBalanca } from "../features/balanca";

const nn = (c) => String(c?.numero || 0).padStart(2, "0");

/**
 * Módulo Comandas (24/10/2026): conta aberta em nome de um cliente (opcional),
 * sem mesa. Lista das abertas (cartão no molde da mesa: ⋮ com 2ª vias e
 * cancelar, olhinho, Imprimir/Fechar comanda → em fechamento) → comanda aberta
 * (lançar, cancelar item, transferir, juntar) → pagamento com a mesma janela
 * do Delivery e das Mesas. Backend: api/v1/comandas.
 */
export default function Comandas({ setHeaderHidden }) {
  const { abertas, carregando, recarregar } = useComandas(true);
  const [selecionada, setSelecionada] = useState(null); // comanda aberta na tela de lançar (com pedido)
  const [cardapio, setCardapio] = useState({ produtos: [], categorias: [], allProducts: [], promos: [] });
  // Cardápio da comanda aberta: cards cinza só até a 1ª leitura (as releituras ao abrir outra comanda não piscam)
  const [cardapioCarregado, setCardapioCarregado] = useState(false);
  const [formas, setFormas] = useState([]);
  const [imprime, setImprime] = useState({ preparo: true, fechamento: true });
  const [abrirAberto, setAbrirAberto] = useState(false);
  const [editarAberto, setEditarAberto] = useState(false);
  const [pagando, setPagando] = useState(null); // comanda (com pedido) na janela de pagamento
  const [vendo, setVendo] = useState(null); // olhinho
  const [aCancelar, setACancelar] = useState(null);
  const [aJuntar, setAJuntar] = useState(null); // ⋮ → Juntar comandas (destino)
  const darkMode = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  // Pesagem rápida (26/10/2026): só com balança neste computador e o módulo Comandas ligado em Preferências → Balança
  const cfgBalanca = useConfigBalanca();
  const prefsBalanca = usePrefsBalanca();
  const temBalanca = balancaLigadaAqui(cfgBalanca) && (prefsBalanca?.modulos || []).includes("Comandas");

  useEffect(() => { setHeaderHidden?.(false); return () => setHeaderHidden?.(false); }, [setHeaderHidden]);

  const carregarCardapio = useCallback(async () => {
    try {
      const [produtos, categorias, allProducts, promos] = await comandasApi.cardapio();
      setCardapio({ produtos: produtos || [], categorias: categorias || [], allProducts: allProducts || [], promos: promos || [] });
    } catch (e) {
      toast.error(erroDe(e, "Não foi possível carregar o cardápio"));
    } finally {
      setCardapioCarregado(true);
    }
  }, []);
  useEffect(() => {
    carregarCardapio();
    comandasApi.formasDePagamento().then((l) => setFormas((l || []).map((pm) => ({ id: pm.code || pm.id, code: pm.code, label: pm.name || pm.code })))).catch(() => setFormas([]));
    comandasApi.imprimePreparo().then((d) => d?.ligados && setImprime((i) => ({ ...i, preparo: d.ligados.Comandas !== false })));
    comandasApi.imprimeFechamento().then((d) => d?.ligados && setImprime((i) => ({ ...i, fechamento: d.ligados.Comandas !== false })));
  }, [carregarCardapio]);

  // As ações do cartão trabalham com a comanda completa (com o pedido)
  const detalheDe = async (c, falha) => {
    try { return await comandasApi.obter(c.id); }
    catch (e) { toast.error(erroDe(e, falha)); return null; }
  };

  const abrirDetalhe = async (c) => {
    const d = await detalheDe(c, "Não foi possível abrir a comanda");
    if (d) { setVendo(null); setSelecionada(d); carregarCardapio(); }
  };
  const atualizar = (detalhe) => { if (detalhe) setSelecionada(detalhe); recarregar(); };
  const sair = () => { setSelecionada(null); recarregar(); };

  // Nova comanda: com balança, carrega o cardápio para achar o produto da pesagem
  const novaComanda = () => { setAbrirAberto(true); if (temBalanca) carregarCardapio(); };
  // Enter na lista abre a Nova comanda (dono, 26/10/2026: controlar pelo teclado).
  // Não vale digitando num campo (a busca) nem com outra janela aberta.
  useEffect(() => {
    if (selecionada) return undefined;
    const tecla = (e) => {
      if (e.key !== "Enter" || e.repeat || e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.target?.closest?.("input, textarea, select, button, a, [contenteditable=true]")) return;
      if (document.querySelector("[role=dialog], [role=alertdialog]")) return;
      e.preventDefault();
      novaComanda();
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [selecionada, temBalanca]); // eslint-disable-line react-hooks/exhaustive-deps
  const editar = async (body) => {
    try { atualizar(await comandasApi.editar(selecionada.id, body)); toast.success("Comanda atualizada"); }
    catch (e) { toast.error(erroDe(e, "Não foi possível salvar")); throw e; }
  };

  const ver = async (c) => { const d = await detalheDe(c, "Não foi possível abrir a comanda"); if (d) setVendo(d); };

  const imprimir = async (c, tipo) => {
    const d = await detalheDe(c, "Não foi possível imprimir");
    if (!d) return;
    try {
      if (tipo === "fechamento" || tipo === "ambos") await imprimirFechamentoDaComanda(d, cardapio.allProducts);
      if (tipo === "preparo" || tipo === "ambos") await imprimirPreparoDaComanda(d, cardapio.allProducts);
    } catch (e) { toast.error(e?.message || "Erro ao imprimir"); }
  };

  // Imprimir (cupom de fechamento) ou Fechar comanda (sem cupom): a comanda
  // fica em fechamento, aguardando pagamento — como a mesa.
  const porEmFechamento = async (c, comCupom) => {
    try {
      const d = await comandasApi.emFechamento(c.id, true);
      toast.success(`Comanda ${nn(c)} em fechamento`);
      recarregar();
      if (comCupom) imprimirFechamentoDaComanda(d, cardapio.allProducts).catch((e) => toast.error(e?.message || "Erro ao imprimir"));
    } catch (e) { toast.error(erroDe(e, "Não foi possível pôr em fechamento")); }
  };
  const reabrir = async (c) => {
    try { await comandasApi.emFechamento(c.id, false); toast.success(`Comanda ${nn(c)} reaberta`); recarregar(); }
    catch (e) { toast.error(erroDe(e, "Não foi possível reabrir")); }
  };
  const pagar = async (c) => { const d = await detalheDe(c, "Não foi possível abrir o pagamento"); if (d?.pedido) setPagando(d); };

  // Juntar pelos ⋮ do cartão: as escolhidas vêm para esta (a mesma rota de dentro da comanda)
  const juntar = async (ids) => {
    const c = aJuntar;
    try {
      await comandasApi.juntar(c.id, ids);
      toast.success(`${ids.length} ${ids.length === 1 ? "comanda juntada" : "comandas juntadas"} na ${nn(c)}`);
      recarregar();
    } catch (e) { toast.error(erroDe(e, "Não foi possível juntar")); throw e; }
  };

  const cancelar = async (motivo) => {
    const c = aCancelar;
    try {
      await comandasApi.cancelar(c.id, motivo);
      toast.success(`Comanda ${nn(c)} cancelada`);
      if (selecionada?.id === c.id) setSelecionada(null);
      recarregar();
    } catch (e) { toast.error(erroDe(e, "Não foi possível cancelar")); throw e; }
  };

  // Pagamento: a janela genérica monta a conta; o backend recebe, conclui pela
  // transição oficial (caixa, cashback, cartão, nota) e marca a comanda fechada.
  const concluir = async (conta) => {
    const c = pagando;
    const pedido = c.pedido;
    const payload = { ...fechamentoDoPedido(conta, pedido), pagamentos_log: conta.log };
    const detalhe = await comandasApi.fechar(c.id, payload);
    toast.success(`Comanda ${nn(c)} fechada · R$ ${Number(detalhe.total || 0).toFixed(2).replace(".", ",")}`);
    if (imprime.fechamento) {
      imprimirFechamentoDaComanda({ ...c, total: detalhe.total, closed_at: detalhe.closed_at }, cardapio.allProducts, conta.log,
        detalhe.pedido || pedido).catch(() => {});
    }
    setPagando(null);
    sair();
  };

  const outras = abertas.filter((c) => c.id !== selecionada?.id);

  return (
    <div data-e2e="comandas-page">
      {selecionada ? (
        <ComandaAberta comanda={selecionada} produtos={cardapio.produtos} categorias={cardapio.categorias} allProducts={cardapio.allProducts}
          activePromotions={cardapio.promos} outras={outras} darkMode={darkMode} imprimePreparo={imprime.preparo}
          carregandoProdutos={!cardapioCarregado}
          onVoltar={sair} onAtualizar={atualizar} onEditar={() => setEditarAberto(true)} onFechar={() => setPagando(selecionada)} onSair={sair} />
      ) : (
        <ListaComandas abertas={abertas} carregando={carregando} onNova={novaComanda} onRecarregar={recarregar}
          onAbrir={abrirDetalhe} onVer={ver} onCancelar={setACancelar} onImprimir={imprimir} onJuntar={setAJuntar}
          onEmFechamento={porEmFechamento} onReabrir={reabrir} onPagar={pagar}
          />
      )}

      {/* Nova comanda (26/10/2026): um botão só para abrir e, com balança, pesar o prato */}
      <NovaComandaDialog open={abrirAberto} onClose={() => setAbrirAberto(false)} abertas={abertas} produtos={cardapio.produtos}
        temBalanca={temBalanca} onLancado={recarregar}
        onEntrar={(detalhe) => { if (detalhe) { recarregar(); setSelecionada(detalhe); carregarCardapio(); } }} />
      <AbrirComandaDialog open={editarAberto} onClose={() => setEditarAberto(false)} inicial={selecionada} onConfirmar={editar} />
      <VerComandaDialog detalhe={vendo} allProducts={cardapio.allProducts} onClose={() => setVendo(null)} onAdicionar={abrirDetalhe} />
      <JuntarDialog comanda={aJuntar} outras={abertas.filter((c) => c.id !== aJuntar?.id)} open={!!aJuntar}
        onClose={() => setAJuntar(null)} onConfirmar={juntar} />
      <CancelarComandaDialog comanda={aCancelar} open={!!aCancelar} onClose={() => setACancelar(null)} onConfirmar={cancelar} />
      {pagando?.pedido && (
        <FechamentoPedidoDialog pedido={{ ...pagando.pedido, cliente_nome: pagando.cliente_nome || pagando.pedido.cliente_nome }}
          formas={formas} open={!!pagando} onOpenChange={(v) => { if (!v) setPagando(null); }} onConcluir={concluir} />
      )}
    </div>
  );
}
